import { forwardRef, HTMLAttributes } from 'react';
import { m, type HTMLMotionProps } from 'framer-motion';
import { clsx } from 'clsx';

export type CardVariant = 'default' | 'elevated' | 'outline';

// Omit HTML animation events that conflict with Framer Motion
type MotionSafeHTMLAttributes<T> = Omit<
  HTMLAttributes<T>,
  'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

export interface CardProps extends MotionSafeHTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hoverable?: boolean;
  as?: 'div' | 'article' | 'section';
}

const variantStyles: Record<CardVariant, string> = {
  default: 'bg-white',
  elevated: 'bg-white shadow-lg',
  outline: 'bg-white border border-text/10',
};

const paddingStyles: Record<string, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      hoverable = false,
      as = 'div',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const Component = m[as] as typeof m.div;

    return (
      <Component
        ref={ref}
        whileHover={hoverable ? { y: -4, boxShadow: '0 12px 40px rgba(0,0,0,0.12)' } : undefined}
        transition={{ duration: 0.2 }}
        className={clsx(
          'rounded-xl overflow-hidden',
          variantStyles[variant],
          paddingStyles[padding],
          hoverable && 'cursor-pointer transition-shadow duration-200',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Card.displayName = 'Card';

// Card sub-components
export interface CardImageProps extends HTMLAttributes<HTMLDivElement> {
  src: string;
  alt: string;
  aspectRatio?: 'video' | 'square' | 'portrait' | 'auto';
}

const aspectStyles: Record<string, string> = {
  video: 'aspect-video',
  square: 'aspect-square',
  portrait: 'aspect-[3/4]',
  auto: '',
};

export const CardImage = forwardRef<HTMLDivElement, CardImageProps>(
  ({ src, alt, aspectRatio = 'video', className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('relative overflow-hidden', aspectStyles[aspectRatio], className)}
        {...props}
      >
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }
);

CardImage.displayName = 'CardImage';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('p-4', className)} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <h3 ref={ref} className={clsx('text-lg font-semibold text-text', className)} {...props}>
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <p ref={ref} className={clsx('text-sm text-text/70 mt-1', className)} {...props}>
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';
